import { ReactNode, useCallback, useRef, useState } from 'react';

import type { Location } from 'history';
import { Prompt, useHistory } from 'react-router-dom';

import DiscardChangesModalV2 from '~/components/common/DiscardChangesModalV2';

interface UseUnsavedChangesGuardOptions {
  isDirty: boolean;
  onDiscard?: () => void;
  shouldBlock?: (destinationPath: string) => boolean;
}

interface UseUnsavedChangesGuardReturn {
  UnsavedChangesModal: ReactNode;
  guardedDismiss: () => void;
}

const useUnsavedChangesGuard = ({
  isDirty,
  onDiscard,
  shouldBlock,
}: UseUnsavedChangesGuardOptions): UseUnsavedChangesGuardReturn => {
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [blockedLocation, setBlockedLocation] = useState<Location | null>(null);
  const allowNavigationRef = useRef(false);

  const promptMessage = useCallback(
    (location: Location): boolean => {
      if (allowNavigationRef.current) return true;
      if (isDirty) {
        if (shouldBlock && !shouldBlock(location.pathname)) return true;
        setBlockedLocation(location);
        setIsModalOpen(true);
        return false;
      }
      return true;
    },
    [isDirty, shouldBlock],
  );

  const closeModal = useCallback(() => {
    setBlockedLocation(null);
    setIsModalOpen(false);
  }, []);

  const confirmDiscard = useCallback(() => {
    const destination = blockedLocation;
    setIsModalOpen(false);
    setBlockedLocation(null);
    onDiscard?.();
    if (destination) {
      allowNavigationRef.current = true;
      history.push(destination);
      allowNavigationRef.current = false;
    }
  }, [blockedLocation, history, onDiscard]);

  const guardedDismiss = useCallback(() => {
    if (isDirty) {
      setIsModalOpen(true);
    } else {
      onDiscard?.();
    }
  }, [isDirty, onDiscard]);

  const UnsavedChangesModal = (
    <>
      <Prompt message={promptMessage} when={isDirty} />
      <DiscardChangesModalV2
        body={blockedLocation ? "You're exiting this page without saving your changes." : undefined}
        isOpen={isModalOpen}
        onClose={closeModal}
        onConfirm={confirmDiscard}
        title={blockedLocation ? 'Warning' : undefined}
      />
    </>
  );

  return {
    UnsavedChangesModal,
    guardedDismiss,
  };
};

export default useUnsavedChangesGuard;
